import type { Howl } from 'howler';
import { create } from 'zustand';

export type MusicContext = 'naming' | 'area' | 'combat' | 'none';

export interface MusicTrack {
  title: string;
  urls: string[];
}

export enum CanSkipTracks {
  Yes = 1,
  No = 0,
}

interface MusicState {
  currentHowl?: Howl;
  context: MusicContext;
  playlist: MusicTrack[];
  currentTrackIndex: number;
  isPlaying: boolean;
  canSkipTracks: CanSkipTracks;
  loopDelayEndsAt: number | null; // timestamp (Date.now() + delayMs), null when not waiting
}

interface MusicActions {
  setCurrentHowl: (howl: Howl | undefined) => void;
  setContext: (context: MusicContext) => void;
  setPlaylist: (playlist: MusicTrack[]) => void;
  setCurrentTrackIndex: (index: number) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setCanSkipTracks: (canSkip: CanSkipTracks) => void;
  setLoopDelayEndsAt: (endsAt: number | null) => void;
  resetMusic: () => void;
}

type MusicStore = MusicState & MusicActions;

const initialState: MusicState = {
  currentHowl: undefined,
  context: 'none',
  playlist: [],
  currentTrackIndex: 0,
  isPlaying: false,
  canSkipTracks: CanSkipTracks.No,
  loopDelayEndsAt: null,
};

export const musicStore = create<MusicStore>((set, get) => ({
  ...initialState,

  setCurrentHowl: (howl): void => {
    const current = get().currentHowl;
    if (current && current !== howl) {
      current.stop();
      current.unload();
    }
    set({ currentHowl: howl });
  },

  setContext: (context): void => {
    set({ context });
  },

  setPlaylist: (playlist): void => {
    set({ playlist, currentTrackIndex: 0 });
  },

  setCurrentTrackIndex: (index): void => {
    set({ currentTrackIndex: index });
  },

  setIsPlaying: (isPlaying): void => {
    set({ isPlaying });
  },

  setCanSkipTracks: (canSkip): void => {
    set({ canSkipTracks: canSkip });
  },

  setLoopDelayEndsAt: (endsAt): void => {
    set({ loopDelayEndsAt: endsAt });
  },

  resetMusic: (): void => {
    const current = get().currentHowl;
    if (current) {
      current.stop();
      current.unload();
    }
    set({ ...initialState, currentHowl: undefined });
  },
}));
